import { dbToGain, TURN_CALL_GAP_SEC, turnCallDelaySec } from './levels';
import { clipSources } from './cues';

export { TURN_CALL_GAP_SEC, turnCallDelaySec };

/** Player turn start, and the rival's turn start. */
export type TurnCall = 'player' | 'rival';

/**
 * Three takes each. A turn start picks one at random and does not repeat
 * the take that seat just used.
 */
export const TURN_CALL_TAKES: Record<TurnCall, readonly string[]> = {
  player: ['vo-your-move-1', 'vo-your-move-2', 'vo-your-move-3'],
  rival: ['vo-opponents-move-1', 'vo-opponents-move-2', 'vo-opponents-move-3'],
};

/**
 * The ogg masters sit under the -13.7 LUFS voice target (about -14.0 to -15.2
 * integrated). +1 dB closes most of that. vo-your-move-2 measures -15.1, so
 * +1 dB would leave it about 0.4 dB short; +2 dB brings it up with the set.
 * True peak on that take is -2.3 dBFS, so +2 dB stays under 0 dBTP.
 */
export const TURN_CALL_GAIN_DB = 1;
export const TURN_CALL_QUIET_FILE = 'vo-your-move-2';
export const TURN_CALL_QUIET_DB = 2;

/**
 * "Opponent's turn!" (ogg + m4a). The master is already a little louder than
 * the move takes, so it plays at unity instead of their +1 dB.
 */
export const OPPONENT_TURN_FILE = 'vo-opponents-turn-1';

export function turnCallTakes(which: TurnCall): readonly string[] {
  return TURN_CALL_TAKES[which];
}

export function turnCallGainDb(file: string): number {
  if (file === OPPONENT_TURN_FILE) return 0;
  return file === TURN_CALL_QUIET_FILE ? TURN_CALL_QUIET_DB : TURN_CALL_GAIN_DB;
}

export function turnCallGain(file: string): number {
  return dbToGain(turnCallGainDb(file));
}

/**
 * Uniform pick from the seat's three takes, skipping the one that just played.
 * A one-take pool would have to repeat.
 */
export function pickTurnCall(which: TurnCall, last: string | null, random: () => number): string {
  const pool = TURN_CALL_TAKES[which];
  const available = pool.filter((file) => file !== last);
  const choices = available.length > 0 ? available : pool;
  const roll = random();
  const index = Math.min(choices.length - 1, Math.max(0, Math.floor(roll * choices.length)));
  return choices[index];
}

/**
 * Phrases a seat can open a turn with.
 * The player uses `move` | `turn`. The rival uses `move` | `opponents-turn`.
 */
export type TurnPhrase = 'move' | 'turn' | 'opponents-turn';

/**
 * Rival openers: the bright "Opponent's move!" takes, and "Opponent's turn!".
 * vo-02-their-move stays out of this pool. It is a softer take.
 */
export function rivalPhraseList(): readonly TurnPhrase[] {
  return ['move', 'opponents-turn'];
}

/** Phrase heard on the previous turns for one seat. `run` is how many times it has repeated. */
export interface TurnPhraseState {
  phrase: TurnPhrase | null;
  run: number;
}

/** A fourth copy of the same phrase is replaced with the other one. */
export const TURN_PHRASE_MAX_RUN = 3;

/**
 * 50/50 between the two phrases. `random` in [0, 0.5) is "move", and [0.5, 1) is "turn".
 * Once a phrase has already been heard TURN_PHRASE_MAX_RUN times in a row, the next
 * pick is the other phrase even if the roll lands on it again.
 */
export function pickTurnPhrase(last: TurnPhrase | null, run: number, random: () => number): TurnPhrase {
  const rolled: TurnPhrase = random() < 0.5 ? 'move' : 'turn';
  if (last !== null && rolled === last && run >= TURN_PHRASE_MAX_RUN) {
    return last === 'move' ? 'turn' : 'move';
  }
  return rolled;
}

/** Remember the phrase that actually started, so the next pick can see the run. */
export function noteTurnPhrase(state: TurnPhraseState, phrase: TurnPhrase): TurnPhraseState {
  return { phrase, run: state.phrase === phrase ? state.run + 1 : 1 };
}

/**
 * Uniform pick from a phrase list. A phrase that has already run
 * TURN_PHRASE_MAX_RUN times yields the next phrase in the list.
 * Two phrases match `pickTurnPhrase`: below 0.5 is the first, 0.5 and up is the second.
 */
export function pickListedPhrase(
  phrases: readonly TurnPhrase[],
  last: TurnPhrase | null,
  run: number,
  random: () => number,
): TurnPhrase {
  const count = phrases.length;
  const first = phrases[0] ?? 'move';
  if (count <= 1) return first;
  const roll = random();
  const index = Math.min(count - 1, Math.max(0, Math.floor(roll * count)));
  let phrase = phrases[index] ?? first;
  if (last !== null && phrase === last && run >= TURN_PHRASE_MAX_RUN) {
    const at = Math.max(0, phrases.indexOf(last));
    phrase = phrases[(at + 1) % count] ?? first;
  }
  return phrase;
}

/** Rival opener. 50/50 between "Opponent's move!" and "Opponent's turn!". */
export function pickRivalPhrase(
  last: TurnPhrase | null,
  run: number,
  random: () => number,
): TurnPhrase {
  return pickListedPhrase(rivalPhraseList(), last, run, random);
}

/**
 * Studio delivery is ogg then m4a. An mp3 of the same name is also accepted
 * so a later voice render can land without a loader change.
 */
export function turnCallSources(file: string, support: { ogg: boolean; m4a: boolean }): string[] {
  const studio = clipSources(file, support);
  const mp3 = `/audio/${file}.mp3`;
  if (studio.includes(mp3)) return studio;
  return [...studio, mp3];
}
